// React-Query hooks for the LLM metering dashboard. Backend lives in
// src/api/routers/metering.py and src/services/metering/service.py.

import { useQuery } from '@tanstack/react-query';
import ApiService from './api.service';

export type CostSummary = {
  start: string;
  end: string;
  thread_count: number;
  message_count: number;
  input_tokens: number;
  output_tokens: number;
  // Reasoning tokens: a subset of output_tokens, not added to total_tokens.
  thinking_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  total_tokens: number;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_creation_cost_usd: number;
  cache_read_cost_usd: number;
  llm_cost_usd: number;
  // Snowflake credits consumed by Cortex — for comparing against METERING_HISTORY /
  // Snowsight Cost Management, which report in credits independent of $/credit.
  input_credits: number;
  output_credits: number;
  cache_creation_credits: number;
  cache_read_credits: number;
  llm_credits: number;
  // Model names we saw usage for but have no rate card for — those rows
  // contributed $0 to totals. UI should warn so the operator adds a card.
  unrated_models?: string[];
  // LLM-only here — the warehouse half comes from useMeteringWarehouse and
  // the page adds the two (see combineCost).
  total_cost_usd: number;
  // LLM spend from background jobs (no chat thread): semantic gap
  // consolidation, connector column suggestions, etc. Included in the grand
  // totals above, broken out here so it's visible rather than vanishing.
  background_cost_usd: number;
  background_message_count: number;
  background_total_tokens: number;
  background_by_source: Array<{
    source: string;
    total_cost_usd: number;
    message_count: number;
    total_tokens: number;
  }>;
  // Tenant's IANA timezone (from build_schedule); the date controls align and
  // label to this calendar day rather than UTC. Falls back to "UTC".
  timezone?: string;
};

export type ThreadCostRow = {
  thread_id: string;
  actor_id: string | null;
  actor_label: string | null;
  agent_type: string | null;
  branch: string | null;
  models: string[];
  first_message_at: string | null;
  last_message_at: string | null;
  input_tokens: number;
  output_tokens: number;
  thinking_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  total_tokens: number;
  message_count: number;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_creation_cost_usd: number;
  cache_read_cost_usd: number;
  llm_cost_usd: number;
  warehouse_credits: number;
  warehouse_cost_usd: number;
  warehouse_query_count: number;
  total_cost_usd: number;
};

export type ThreadTurn = {
  turn_index: number;
  created_at: string;
  model_name: string;
  branch: string | null;
  stop_reason: string | null;
  input_tokens: number;
  output_tokens: number;
  thinking_tokens: number;
  // Non-thinking output, in characters (not tokens): visible text vs tool-call arguments.
  text_chars: number;
  tool_call_chars: number;
  tool_call_names: string[];
  has_thinking_text: boolean;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
  total_tokens: number;
  message_count: number;
  input_cost_usd: number;
  output_cost_usd: number;
  cache_creation_cost_usd: number;
  cache_read_cost_usd: number;
  total_cost_usd: number;
};

export type WarehouseQuery = {
  query_id: string;
  start_time: string | null;
  end_time: string | null;
  warehouse_name: string | null;
  credits: number;
  cost_usd: number;
  query_text: string;
  elapsed_ms: number;
  execution_status: string | null;
};

export type ToolCallSummary = {
  name: string;
  count: number;
  total_result_chars: number;
  max_result_chars: number;
  total_result_tokens_est: number;
  max_result_tokens_est: number;
};

export type ToolCallDetail = {
  name: string;
  id: string | null;
  turn_index: number;
  args_chars: number;
  result_chars: number;
  result_tokens_est: number;
};

export type ThreadDetail = ThreadCostRow & {
  thread_name: string | null;
  agent_type: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  turns: ThreadTurn[];
  tool_calls: ToolCallSummary[];
  heaviest_tool_calls?: ToolCallDetail[];
  unrated_models?: string[];
};

// Free-text per-turn detail, Admin-only endpoint (may contain tenant data).
export type ThreadTurnOutput = {
  thread_id: string;
  // False when the checkpoint's AI messages couldn't be matched 1:1 to the
  // usage rows; output_text/tool_calls are then absent rather than risk
  // showing another turn's output.
  output_aligned: boolean;
  usage_turn_count: number;
  message_turn_count: number;
  turns: Array<{
    turn_index: number;
    thinking_text: string | null;
    thinking_tokens: number;
    output_text?: string;
    tool_calls?: Array<{ name: string | null; args: string }>;
  }>;
};

export type ThreadWarehouseDetail = {
  thread_id: string;
  warehouse_queries: WarehouseQuery[];
  warehouse_unavailable?: boolean;
  warehouse_credits: number;
  warehouse_cost_usd: number;
  warehouse_query_count: number;
  currency?: string;
  usd_per_credit?: number;
};

export type ThreadCostPage = {
  threads: ThreadCostRow[];
  total_count: number;
  limit: number;
  offset: number;
  sort: string;
};

export type CostRange =
  | 'last_24h'
  | 'previous_day'
  | 'previous_7d'
  | 'previous_30d';

type CommonParams = {
  range?: CostRange;
  agentType?: string;
  // Explicit ISO window. When either is set, `range` is omitted from the
  // request so the backend resolves the custom [from, to) window instead of a
  // keyword. Used by the "Custom range" picker for SCT reconciliation.
  from?: string;
  to?: string;
};

const toQueryParams = (params: Record<string, any>) => {
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
};

// Warehouse-compute half of the dashboard headline, from Snowflake
// QUERY_ATTRIBUTION_HISTORY. Fetched separately from the LLM summary because
// it's slow (a Snowflake scan) and up to 8h behind; the LLM cards don't wait.
export type WarehouseSummary = {
  warehouse_credits: number;
  warehouse_qas_credits: number;
  warehouse_cost_usd: number;
  warehouse_query_count: number;
  // True when the calling Snowflake role lacks SNOWFLAKE.ACCOUNT_USAGE access;
  // warehouse_* fields are zeroed in that case and the dashboard should call
  // it out instead of pretending the warehouse cost is genuinely $0.
  warehouse_unavailable?: boolean;
  // The tenant's configured Snowflake role, surfaced so the UI can show the
  // exact GRANT statement to run. Null if we couldn't resolve it.
  warehouse_role?: string | null;
};

// Grand total and per-thread average across both halves. Warehouse credits
// are thread-tagged by definition, so the per-thread average excludes only
// the LLM background-job spend from the numerator.
export const combineCost = (summary: CostSummary, warehouse: WarehouseSummary) => {
  const total = summary.llm_cost_usd + warehouse.warehouse_cost_usd;
  const threadCost = total - (summary.background_cost_usd ?? 0);
  return {
    totalCostUsd: total,
    avgCostPerThreadUsd: summary.thread_count ? threadCost / summary.thread_count : 0,
  };
};

export const useMeteringWarehouse = ({ range = 'previous_30d', from, to }: CommonParams = {}) => {
  const custom = !!(from || to);
  return useQuery<WarehouseSummary>({
    queryKey: ['cost-warehouse', custom ? 'custom' : range, from ?? null, to ?? null],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        '/metering/llm/warehouse',
        toQueryParams({ range: custom ? undefined : range, from, to }),
      );
      return result as WarehouseSummary;
    },
    // The backend caches the Snowflake scan for an hour and the data lags up
    // to 8h, so there's no point refetching often.
    staleTime: 5 * 60 * 1000,
  });
};

export const useMeteringSummary = ({ range = 'previous_30d', agentType, from, to }: CommonParams = {}) => {
  const custom = !!(from || to);
  return useQuery<CostSummary>({
    queryKey: ['cost-summary', custom ? 'custom' : range, from ?? null, to ?? null, agentType ?? null],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        '/metering/llm/summary',
        // `range` and `from`/`to` are mutually exclusive on the backend (range
        // wins), so drop `range` when a custom window is active.
        toQueryParams({ range: custom ? undefined : range, from, to, agent_type: agentType }),
      );
      return result as CostSummary;
    },
    // LLM numbers are real-time; a minute is fresh enough for a dashboard.
    staleTime: 60 * 1000,
  });
};

export const useMeteringThreadDetail = (threadId: string | undefined) => {
  return useQuery<ThreadDetail>({
    queryKey: ['cost-thread-detail', threadId],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        `/metering/llm/threads/${threadId}`,
      );
      return result as ThreadDetail;
    },
    enabled: !!threadId,
    staleTime: 60 * 1000,
  });
};

// Reasoning summaries + per-turn output. Lazy (`enabled`): only fetched once the
// user expands a turn, since it is Admin-only and carries free text.
export const useMeteringThreadTurnOutput = (threadId: string | undefined, enabled: boolean) => {
  return useQuery<ThreadTurnOutput>({
    queryKey: ['cost-thread-turn-output', threadId],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        `/metering/llm/threads/${threadId}/turn-output`,
      );
      return result as ThreadTurnOutput;
    },
    enabled: !!threadId && enabled,
    staleTime: 60 * 1000,
    retry: false,
  });
};

// Warehouse-cost data lives behind a separate endpoint because the Snowflake
// QUERY_ATTRIBUTION_HISTORY query takes 1–4s. We fire this in parallel with
// useMeteringThreadDetail so the LLM-side data renders immediately while the
// warehouse section gets its own spinner.
export const useMeteringThreadWarehouse = (threadId: string | undefined) => {
  return useQuery<ThreadWarehouseDetail>({
    queryKey: ['cost-thread-warehouse', threadId],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        `/metering/llm/threads/${threadId}/warehouse`,
      );
      return result as ThreadWarehouseDetail;
    },
    enabled: !!threadId,
    // Warehouse data lags ~3hr behind real-time (ACCOUNT_USAGE latency), so
    // a longer staleTime is fine here than for the LLM half.
    staleTime: 5 * 60 * 1000,
  });
};

export const useMeteringThreads = (
  {
    range = 'previous_30d',
    agentType,
    from,
    to,
    limit = 50,
    offset = 0,
    sort = 'cost_desc',
    search,
  }: CommonParams & { limit?: number; offset?: number; sort?: string; search?: string } = {},
) => {
  const custom = !!(from || to);
  return useQuery<ThreadCostPage>({
    queryKey: ['cost-threads', custom ? 'custom' : range, from ?? null, to ?? null, agentType ?? null, limit, offset, sort, search ?? null],
    queryFn: async () => {
      const result = await ApiService.getInstance().request(
        'GET',
        '/metering/llm/threads',
        toQueryParams({
          // range and from/to are mutually exclusive (backend prefers range).
          range: custom ? undefined : range,
          from,
          to,
          agent_type: agentType,
          limit,
          offset,
          sort,
          search,
        }),
      );
      return result as ThreadCostPage;
    },
    staleTime: 60 * 1000,
  });
};
